import { useLayoutEffect, useRef } from "react";

import { Typography } from "@bigbinary/neeto-atoms";
import classnames from "classnames";
import { noop, isNotEmpty, isPresent } from "neetocist";
import PropTypes from "prop-types";

import { DROPDOWN_BUTTON_SIZES, SIZES } from "./constants";

import Breadcrumbs from "../Breadcrumbs";
import HelpPopover from "../HelpPopover";
import MoreDropdown from "../MoreDropdown";
import Search from "../Search";

const Header = ({
  title,
  titleHelpPopoverProps = {},
  searchProps,
  className = "",
  actionBlock,
  breadcrumbs = [],
  renderDropdown = noop,
  size = "small",
  "data-testid": dataTestId = "neeto-molecules-header",
}) => {
  const headerRef = useRef(null);

  useLayoutEffect(() => {
    const updateHeaderHeight = () => {
      if (!headerRef.current) return;
      const height = headerRef.current.offsetHeight;
      document.body.style.setProperty(
        "--neeto-molecules-effective-header-height",
        `${height}px`
      );
    };

    updateHeaderHeight();

    window.addEventListener("resize", updateHeaderHeight);

    return () => {
      window.removeEventListener("resize", updateHeaderHeight);
      document.body.style.removeProperty(
        "--neeto-molecules-effective-header-height"
      );
    };
  }, []);

  const isNano = size === SIZES.nano;
  const hasBreadcrumbs = isNotEmpty(breadcrumbs);
  const TitleTag = isNano ? "h3" : "h1";

  return (
    <div
      data-testid={dataTestId}
      ref={headerRef}
      className={classnames(
        "flex w-full shrink-0 flex-row items-start justify-between gap-4 pt-10 pb-6",
        "flex-wrap lg:flex-nowrap",
        { "min-h-0": isNano || size === SIZES.small },
        className
      )}
    >
      <div className="flex flex-row flex-wrap items-start justify-start">
        <div className="flex flex-wrap">
          {hasBreadcrumbs && <Breadcrumbs {...{ breadcrumbs }} />}
          <div
            className={classnames(
              "flex w-full flex-row items-center gap-3",
              hasBreadcrumbs
                ? "mt-0"
                : "-mt-1 max-lg:-mt-2.5 max-md:-mt-2 max-sm:-mt-1"
            )}
          >
            <Typography
              asChild
              variant={isNano ? "h3" : "h1"}
              weight="semibold"
            >
              <TitleTag data-testid="main-header">
                {title}
                {isPresent(titleHelpPopoverProps) && (
                  <HelpPopover
                    className="ms-2 inline-flex align-middle"
                    side="right"
                    {...titleHelpPopoverProps}
                  />
                )}
                {renderDropdown(
                  ({ dropdownButtonProps = {}, ...dropdownProps }) => (
                    <span className="ms-2 inline-flex align-middle">
                      <MoreDropdown
                        dropdownButtonProps={{
                          size: DROPDOWN_BUTTON_SIZES[size],
                          ...dropdownButtonProps,
                        }}
                        {...dropdownProps}
                      />
                    </span>
                  )
                )}
              </TitleTag>
            </Typography>
          </div>
        </div>
      </div>
      <div
        className={classnames(
          "flex items-center gap-3 empty:hidden",
          "flex-wrap justify-start md:flex-nowrap md:justify-end"
        )}
      >
        {searchProps && (
          <Search
            enableUrlSync
            className={classnames(
              "min-w-[12.6rem] grow-0",
              searchProps.className
            )}
            {...searchProps}
          />
        )}
        {actionBlock && (
          <div className="flex shrink-0 items-center justify-end gap-3 max-[360px]:shrink max-[360px]:flex-wrap max-[360px]:justify-start">
            {actionBlock}
          </div>
        )}
      </div>
    </div>
  );
};

Header.propTypes = {
  /**
   * To set a title in the Header section.
   */
  title: PropTypes.node,
  /**
   * To set props for the HelpPopover component in the title.
   */
  titleHelpPopoverProps: PropTypes.object,
  /**
   * To specify additional class names.
   */
  className: PropTypes.string,
  /**
   * To specify the content to be rendered in the right side of the Header section.
   */
  actionBlock: PropTypes.node,
  /**
   * To add **Search** molecule to the subheader section.
   */
  searchProps: PropTypes.object,
  /**
   * To show breadcrumbs in the Header section. `text` accepts a text string to be shown as the breadcrumb and `link` accepts relative URL path for the breadcrumb.
   */
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.string,
    })
  ),
  /**
   * A function to render the Dropdown next to the title. It provides the MoreDropdown component as argument.
   */
  renderDropdown: PropTypes.func,
  /**
   * To specify size of the Header.
   * `small` - Default size.
   *
   * `nano` - To be used in inner pages with menubar.
   */
  size: PropTypes.oneOf(["nano", "small"]),
};

export default Header;
